<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="$t('Generate Batch Telephone Bids')"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :is-loading="modal.loading" scrollable @show="onShow" :content-in-form="false">

      <LotsTable v-show="currentStage === 1" :hide-actions="hideActions" @selectedItemsAllChange="selectedItemsChange"
                 hide-all-actions persistent-selected-items class="mb-3"
                 :exclude-columns="excludeColumns"
                 :exclude-search-fields="excludeFilters"/>

      <b-form @submit.prevent="handleSubmit(onSubmit)" v-if="currentStage === 2">

        <div class="row">
          <div class="col-md-12">
            <div class="d-flex justify-content-center">
              <div class="col-md-8">
                <bidder-select-input-widget type="text" attribute="bidder_uuid" value-field="uuid" :model="model"
                                            @select="onBidderSelect"
                                            :display-field="bidderDisplayField"
                                            :auction-uuid="modal.sale ? modal.sale.uuid : null"/>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-md-12">
            <div v-if="bidder">
              <h5 class="mb-0">
                <router-link tabindex="-1"
                             :to="{ name: 'customers.dashboard', params: { id: bidder.customer.uuid }}">
                  {{ bidder.customer.display_name }}
                </router-link>
              </h5>
              <p class="mb-0" v-if="bidder.customer && bidder.customer.billing_address"
                 v-html="$options.filters.asAddress(bidder.customer.billing_address, false)">
              </p>
              <p class="m-0" v-if="bidder.customer.email">
                {{ bidder.customer.email }}
              </p>
              <p class="m-0" v-if="bidder.language">
                {{ $t("Language") }}: {{ bidder.language.name }}
              </p>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-8 border-right">
            <div class="row">
              <div class="col-sm-12 col-md-6 col-lg-4">
                <currency-input-widget :model="model" :currency="modal.sale ? sale.currency.symbol : null"
                                       attribute="amount"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="status" value-field="value" display-field="${item.key}"
                              type="select2" :items="statuses"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <clerk-select-input-widget :model="model" attribute="clerk_uuid" :auction="sale"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="extra_step" rules="min_value:1|max_value:100"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="phone" :rules="getPhoneRules(model)"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="phone2"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="date" type="datepicker" :rules="model.time ? 'required' : ''"/>
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                <input-widget :model="model" attribute="time" type="timepicker" :rules="model.date ? 'required' : ''"/>
              </div>
              <div class="col-sm-12">
                <input-widget :model="model" type="textarea" attribute="description"/>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card">
              <div class="card-header">
                {{ $t('Selected Lots') }}
              </div>
              <div class="card-body" style="height: 300px;overflow-y: auto">
                <div class="row">
                  <table class="table table-sm mb-0" style="font-size: 11px">
                    <tr>
                      <th class="border-top-0">{{ $t('Lot No') }}</th>
                      <th class="border-top-0">{{ $t('Stock No') }}</th>
                      <th class="border-top-0">{{ $t('Title') }}</th>
                    </tr>
                    <tr v-for="item in selectedItems" :key="`selected-item-row-${item.uuid}`">
                      <td>{{ item.lot_no }}</td>
                      <td>{{ item.inventory.stock_no }}</td>
                      <td>{{ item.inventory.short_description | striptags | truncate(50) }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </b-form>


      <template v-slot:modal-footer>
        <cancel-button @click="onCancel">
          {{ $t('Cancel') }}
        </cancel-button>
        <cancel-button @click="onGoBackClick" v-if="currentStage === 2">
          {{ $t('Go Back') }}
        </cancel-button>
        <submit-button @click="onNextClick" v-if="currentStage === 1">
          {{ $t('Next') }}
        </submit-button>
        <submit-button v-else-if="currentStage === 2"
                       @click="handleSubmit(onSubmit)"
                       :disabled="modalLoading"
                       :show-loader="modalLoading">
          {{ $t('Submit') }}
        </submit-button>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import BatchTelephoneBidGenerationModel from "@/modules/pre-bids/models/BatchTelephoneBidGeneration.model";
  import {
    ACTION_ARCHIVE_LOT, ACTION_DELETE_LOT, ACTION_EXPORT_LOT_IMAGES,
    ACTION_PUBLISH_LOT,
    ACTION_REVERT_TO_COMPLETED_LOT,
    ACTION_SYNC_LOTS
  } from "@/modules/constants";
  import LotsTable from "@/modules/lots/LotsTable";
  import _ from "lodash";
  import CancelButton from "@/core/components/common/CancelButton";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget";
  import i18n from "@/shared/i18n";
  import {getPhoneRuleObj} from "@/plugins/vee-validate";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget";
  import SalesService from "@/modules/sales/SalesService";

  const {mapState, mapActions} = createNamespacedHelpers('preBids');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');

  export default {
    name: "BatchTelephoneBidGenerationModal",
    components: {
      BidderSelectInputWidget,
      ClerkSelectInputWidget,
      CurrencyInputWidget,
      InputWidget,
      SubmitButton,
      CancelButton,
      LotsTable,
      ArtisioModal,
    },
    data() {
      return {
        showConfirmation: true,
        model: new BatchTelephoneBidGenerationModel(),
        hideActions: [
          ACTION_PUBLISH_LOT,
          ACTION_ARCHIVE_LOT,
          ACTION_REVERT_TO_COMPLETED_LOT,
          ACTION_SYNC_LOTS,
          ACTION_EXPORT_LOT_IMAGES,
          ACTION_DELETE_LOT,
        ],
        selectedItems: [],
        currentStage: 1,
        statuses: [
          {key: i18n.t('Pending'), value: 'pending'},
          {key: i18n.t('Confirmed'), value: 'confirmed'},
        ],
        bidder: null,
        excludeColumns: [
          'date', 'sale_no', 'new_low', 'new_high', 'new_reserve', 'new_start_price', 'proposal_sent_at',
          'proposal_sent_by', 'proposal_confirmed_at', 'proposal_confirmed_by'
        ],
        excludeFilters: [
          'start_date', 'end_date', 'status', 'collected', 'unsettled', 'negotiation_status', 'sold_via_negotiation',
          'bidder_uuid', 'invoice_status', 'hide_lot', 'proposal_suggestion', 'proposal_status',
          'auction_collection_uuid', 'sale_no', 'source_of_sale'
        ]
      }
    },
    computed: {
      ...mapState({
        modal: state => state.batchTelephoneBidGenerationModal,
        modalLoading: state => state.batchTelephoneBidGenerationModal.loading,
        sale: state => state.batchTelephoneBidGenerationModal.sale,
      }),
    },
    methods: {
      ...mapActions([
        'hideBatchTelephoneBidGenerationModal', 'getTableData', 'toggleBatchTelephoneBidGenerationModalLoading'
      ]),
      ...lotsMapActions(['updateTableSource']),
      bidderDisplayField(item) {
        if (item.customer) {
          let str =  `${item.paddle_number} - ${item.customer.display_name}`
          if (item.customer.company_name) {
            str = `${str} - ${item.customer.company_name}`
          }
          str = `${str} - ${item.customer.email}`
          return str
        }
        return undefined;

      },
      getPhoneRules(bid) {
        return getPhoneRuleObj(bid.type === 'telephone')
      },
      async onShow() {
        this.showConfirmation = true;
        await this.updateTableSource({
          source: 'batchTelephoneBidsGenerationModal',
          extraFilters: {
            auction_uuid: this.sale.uuid,
            status: 'lotted'
          },
          sale: _.cloneDeep(this.sale)
        })
      },
      onHidden() {
        this.hideBatchTelephoneBidGenerationModal();
        this.model = new BatchTelephoneBidGenerationModel()
        this.selectedItems = []
        this.currentStage = 1
        this.bidder = null
      },
      onNextClick() {
        if (this.selectedItems.length === 0) {
          this.$errorDialog(this.$i18n.t('Please, select at least one item to continue'))
          return false
        } else if (this.selectedItems.length > 20) {
          this.$errorDialog(this.$i18n.t('You cannot create batch telephone bids for more than 20 lots at a time. Please, deselect some items and try again'))
          return false
        }

        this.currentStage = 2
      },
      onGoBackClick() {
        this.currentStage = 1
      },
      async onSubmit() {
        this.toggleBatchTelephoneBidGenerationModalLoading(true)
        let requestData = []
        this.selectedItems.forEach(i => {
          requestData.push({
            lot_no: i.lot_no,
            amount: this.model.amount ? this.model.amount : 0,
            phone: this.model.phone,
            phone2: this.model.phone2,
            clerk_uuid: this.model.clerk_uuid,
            type: this.model.type,
            status: this.model.status,
            description: this.model.description,
            date: this.model.date,
            time: this.model.time,
            extra_step: this.model.extra_step,
            paddle_number: this.bidder.paddle_number,
            sale_no: this.sale.sale_no,
            auction_uuid: this.sale.uuid,
            stop_notification_sending: true
          })
        })

        let response = await SalesService.createPreBids(requestData)
        if (response.success) {
          this.showConfirmation = false
          this.$toast('success', this.$i18n.t('Telephone bids were successfully created'))
          this.getTableData()
          this.hideBatchTelephoneBidGenerationModal()
        } else {
          this.showConfirmation = true
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Telephone Bids Generation Error'));
          } else if (typeof response.body === 'object') {
            let hasNonFieldError = false
            for (let err of response.body) {
              if (err.non_field_errors) {
                hasNonFieldError = true
                break
              }
            }
            if (hasNonFieldError) {
              const listItemsArr = [];
              for (let i in response.body) {
                if (response.body[i].non_field_errors){
                  listItemsArr.push(this.$createElement('li', {}, `Lot No: ${requestData[i].lot_no}. ${response.body[i].non_field_errors}`))
                }
              }
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Telephone Bids Generation Error'));
            }
          }
        }
        this.toggleBatchTelephoneBidGenerationModalLoading(false)

      },
      onCancel() {
        this.hideBatchTelephoneBidGenerationModal()
      },
      selectedItemsChange(selectedItems) {
        this.selectedItems = Array.from(selectedItems).sort((a, b) => {
          const regex = /^(\d+)([A-Za-z]*)$/;

          const [, numA, alphaA] = a.lot_no.match(regex) || [];
          const [, numB, alphaB] = b.lot_no.match(regex) || [];

          const intA = parseInt(numA, 10);
          const intB = parseInt(numB, 10);

          if (intA !== intB) {
            return intA - intB;
          }

          // Compare alphabetically if numeric parts are equal
          return (alphaA || '').localeCompare(alphaB || '');
        });
      },
      onBidderSelect(data) {
        this.bidder = data
        this.model.bidder = data
        this.assignPhoneToModel()
      },
      assignPhoneToModel() {
        if (this.bidder && this.bidder.customer && this.bidder.customer.phone) {
          let phone1 = null;
          let phone2 = null;
          let splitValue = this.bidder.customer.phone.split(',')

          if (splitValue && splitValue.length > 0) {
            phone1 = splitValue[0]
          }
          if (splitValue && splitValue.length > 1) {
            phone2 = splitValue[1]
          }

          this.model.phone = phone1
          this.model.phone2 = phone2
        }
      }

    }
  }
</script>

<style scoped>
</style>
